import { lazy, Suspense, useEffect, useState } from "react";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { useAuth } from "@/hooks/useAuth";
import { FieldValues, useForm, useWatch } from "react-hook-form";

import { RequestTypes } from "../../../../constants/enums";
import { CounterDeliveryDetail } from "../schema/counter-delivery.schema";
import { getCounterDelivery } from "@/services/counter-delivery.service";
import { CounterDeliveryAmountAndActions } from "@/components/common/section-components/requests/counter-delivery/CounterDeliveryAmountAndActions";
import {
  handleExecuteCounterDeliveryHandler,
  handleRejectCounterDeliveryHandler,
  sendCounterDeliveryNegotiationHandler,
  updateCounterDeliveryAmountHandler,
} from "../schema/handler-functions";
import { useRoles } from "@/hooks/useRoles";
import { CounterDeliveryDetailMainDataCard } from "@/components/common/section-components/card/CounterDeliveryMainDataCard";
import { AppColors } from "@/styles/colors";
import { CounterDeliveryDetailLegend } from "@/components/common/section-components/requests/counter-delivery/CounterDeliveryDetailLegend";
import { CounterDeliveryMessageList } from "@/components/common/section-components/requests/counter-delivery/CounterDeliveryMessageList";

export const CounterDeliveryDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.GRAIN_DELIVERY_PAYMENTS);
  };

  const [counterDelivery, setCounterDelivery] =
    useState<CounterDeliveryDetail | null>();
  const fetchCounterDelivery = async (showLoad: boolean = true) => {
    if (showLoad) setLoadingCounterDelivery(true);
    const response = await getCounterDelivery({
      id: Number(id),
      usuario: user?.name ?? "",
    });

    setCounterDelivery(response);

    setLoadingCounterDelivery(false);
  };

  const [loadingCounterDelivery, setLoadingCounterDelivery] =
    useState<boolean>(false);

  useEffect(() => {
    if (isActive && id && user) fetchCounterDelivery();
  }, [isActive, id, user]);

  const [isEditingAmount, setIsEditingAmount] = useState<boolean>(false);
  const [loadingEditAmount, setLoadingEditAmount] = useState<boolean>(false);
  const [loadingSendNegotiation, setLoadingSendNegotiation] =
    useState<boolean>(false);

  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [loadingRejectCounterDelivery, setLoadingRejectCounterDelivery] =
    useState<boolean>(false);
  const [loadingExecuteCounterDelivery, setLoadingExecuteCounterDelivery] =
    useState<boolean>(false);

  const handleExecuteCounterDelivery = async () => {
    await handleExecuteCounterDeliveryHandler({
      navigate,
      setLoadingExecuteCounterDelivery,
      user,
      counterDelivery,
      fetchPermissions,
    });
  };

  const handleRejectCounterDelivery = async (event?: FieldValues) => {
    await handleRejectCounterDeliveryHandler({
      navigate,
      setLoadingRejectCounterDelivery,
      setOpenRejectModal,
      user,
      event,
      counterDelivery,
      fetchPermissions,
    });
  };

  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm();

  const watchedValues = useWatch({
    control,
  });

  const handleEditAmount = async () => {
    await updateCounterDeliveryAmountHandler({
      errors,
      setLoadingEditAmount,
      counterDelivery,
      user: user?.name ?? "",
      fetchCounterDelivery,
      setIsEditingAmount,
      watchedValues,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (counterDelivery && !counterDelivery?.sinBase) {
      setValue("sell_order_amount", counterDelivery?.montoSolicitado);
    }
  }, [counterDelivery]);

  const { setOpen: setOpenNegotiation, open: openNegotiation } =
    useDisclosure();

  const { fetchPermissions } = useRoles();

  const handleSendNegotiation = async (event?: FieldValues) => {
    await sendCounterDeliveryNegotiationHandler({
      navigate,
      setLoadingSendNegotiation,
      setOpenNegotiationModal: setOpenNegotiation,
      user: user?.name ?? "",
      event,
      counterDelivery,
      fetchCounterDelivery,
      fetchPermissions,
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={"Solicitud de venta de granos a entregar"}
        onBack={handleBack}
        backButton
        loading={loadingCounterDelivery}
      />

      {!loadingCounterDelivery && (
        <>
          <CounterDeliveryAmountAndActions
            clearErrors={clearErrors}
            counterDelivery={counterDelivery}
            setIsEditingAmount={setIsEditingAmount}
            control={control}
            errors={errors}
            handleEditAmount={handleEditAmount}
            loadingEditAmount={loadingEditAmount}
            isEditingAmount={isEditingAmount}
            setOpenRejectModal={setOpenRejectModal}
            loadingRejectCounterDelivery={loadingRejectCounterDelivery}
            loadingExecuteCounterDelivery={loadingExecuteCounterDelivery}
            handleExecuteCounterDelivery={handleExecuteCounterDelivery}
            setValue={setValue}
            loadingSendNegotiation={loadingSendNegotiation}
            setOpenInfoModal={setOpenNegotiation}
          />

          <CounterDeliveryDetailMainDataCard
            counterDelivery={counterDelivery}
          />

          <Flex
            flexDir="column"
            padding="12px"
            height="100%"
            border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            borderRadius="12px"
            gap="25px"
          >
            <CounterDeliveryDetailLegend />

            <CounterDeliveryMessageList counterDelivery={counterDelivery} />
          </Flex>
        </>
      )}

      {counterDelivery?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectCounterDelivery}
            loadingAccept={loadingRejectCounterDelivery}
            idTipoSolicitud={RequestTypes.ContraEntrega}
            entityName="venta de granos a entregar"
          />
          <NegotiationModalDynamic
            open={openNegotiation}
            setOpen={setOpenNegotiation}
            handleAccept={handleSendNegotiation}
            idTipoSolicitud={RequestTypes.ContraEntrega}
            loadingAccept={loadingSendNegotiation}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);

const NegotiationModalDynamic = lazy(() =>
  import("@/components/modal/NegotiationModal").then((el) => ({
    default: el["NegotiationModal"],
  })),
);
